'use client';

import React from 'react';
import classNames from 'classnames';
import Image from 'next/image';
import Star from '@/public/assets/icons/star.png';
import usePromoCredits from '@/lib/api/productCatalog/hooks/usePromoCredits';
import useFeature from '@/hooks/useFeature';
import { FeatureFlag } from '@/core/featureFlags';
import interpolate from '@/utils/translations/interpolate';
import { formatMoneyWithSmartDecimals } from '@/utils/helpers';
import useLocale from '@/hooks/useLocale';
import { convertCentsToDollars } from '@/utils/currency';

interface BannerProps {
  userUuid: string;
  subscriptionUuid: string;
  message: string;
  classes?: string;
}

function PromoCreditsBanner({
  userUuid,
  subscriptionUuid,
  message,
  classes,
}: BannerProps) {
  const bannerFlagValue = useFeature(
    FeatureFlag.SUB_SQUAD_BFCM_PROMO_CREDITS_BANNER,
    'off',
  );

  const isBannerEnabled = bannerFlagValue === 'b';

  const locale = useLocale();

  const { data, isLoading, error } = usePromoCredits({
    userUuid,
    shouldFetch: isBannerEnabled,
  });

  if (!isBannerEnabled || isLoading || error) {
    return null;
  }

  const targetPromoCredit = data?.find(
    (promoCredit) => promoCredit.subscription_uuid === subscriptionUuid,
  );

  if (!targetPromoCredit) {
    return null;
  }

  const discountValue = formatMoneyWithSmartDecimals(
    convertCentsToDollars(targetPromoCredit.discount_value),
    locale,
  );

  return (
    <div
      data-testid="bfcm-promo-credits-banner"
      className={classNames(
        'flex items-start p-4 gap-2 border border-success rounded-md bg-kelly-green-tint',
        classes,
      )}
    >
      <Image
        src={Star}
        alt=""
        width={36}
        height={24}
        className="h-8 w-6 shrink-0"
      />

      <p>
        {interpolate(message, {
          discountValue,
        })}
      </p>
    </div>
  );
}

export default PromoCreditsBanner;
